import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Clock, FileText, PencilLine } from 'lucide-react';
import { useKnowledgeBaseTree } from '../../hooks/useKnowledgeBase';
import type { KnowledgeBaseItem } from '../../services/knowledgeBase.service';
import { formatShortDate } from '../../utils/formatDate';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';

/** Pending articles shown inline before we send the user to the full tree. */
const MAX_ROWS = 5;

type PendingArticle = {
  item: KnowledgeBaseItem;
  /** True when this proposes a change to an existing article rather than a new one. */
  isUpdate: boolean;
  /** Where the knowledge base page shows this proposal — the original, for updates. */
  linkId: string;
  folderName: string | null;
};

function PendingRow({ pending }: { pending: PendingArticle }) {
  const { item, isUpdate, linkId, folderName } = pending;
  const Icon = isUpdate ? PencilLine : FileText;
  const proposedOn = formatShortDate(item.created_at);

  return (
    <Link
      to={`/knowledge-base/article/${linkId}`}
      className="flex items-center gap-3 px-6 py-3.5 border-b border-border last:border-b-0 hover:bg-accent/50 transition-colors"
    >
      <div className="h-8 w-8 flex-shrink-0 rounded-md bg-amber-100 dark:bg-amber-950 flex items-center justify-center">
        <Icon className="h-4 w-4 text-amber-700 dark:text-amber-400" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-foreground truncate">{item.name}</span>
          <Badge
            variant="outline"
            className="h-5 px-1.5 text-[11px] font-normal flex-shrink-0 hidden sm:inline-flex"
          >
            {isUpdate ? 'Update' : 'New'}
          </Badge>
        </div>
        {/* `proposed_by` holds the actor id of the session Ellie was running in
            — a user uuid, not a name — so it isn't shown. The knowledge base
            page names Ellie the same way. */}
        <p className="text-xs text-muted-foreground truncate mt-0.5">
          Proposed by Ellie
          {proposedOn ? ` on ${proposedOn}` : ''}
          {folderName ? ` · ${folderName}` : ''}
        </p>
      </div>

      <span className="flex-shrink-0 text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1">
        <Clock className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Needs review</span>
      </span>
    </Link>
  );
}

/**
 * "Knowledge Base" — the articles Ellie has proposed and nobody has approved or
 * rejected yet. Both shapes of proposal land here: a brand new article
 * (`status: 'pending'`, no original) and a rewrite of an existing one (a pending
 * clone carrying `original_item_id`).
 */
export default function KnowledgeBaseAttention() {
  const { data, isLoading, error } = useKnowledgeBaseTree();

  const pending = useMemo<PendingArticle[]>(() => {
    const items = data?.records || [];
    const nameById = new Map(items.map((i) => [i.id, i.name]));

    const rows = items
      .filter((i) => i.status === 'pending' && i.item_type === 'article')
      // An update whose original is gone has nowhere to link and no diff to
      // review — the sidebar badge skips those too, so the two agree.
      .filter((i) => !i.original_item_id || nameById.has(i.original_item_id))
      .map((item) => ({
        item,
        isUpdate: !!item.original_item_id,
        // An update clone is hidden from the tree — the page surfaces it on the
        // original, so that's where the link has to point.
        linkId: item.original_item_id || item.id,
        folderName: item.parent_id ? nameById.get(item.parent_id) ?? null : null,
      }))
      .sort(
        (a, b) =>
          Date.parse(b.item.created_at || '') - Date.parse(a.item.created_at || '') || 0,
      );

    // Two proposals against the same original are one thing to review, and two
    // rows linking to the same place would just look like a duplicate.
    const byLinkId = new Map<string, PendingArticle>();
    for (const row of rows) {
      if (!byLinkId.has(row.linkId)) byLinkId.set(row.linkId, row);
    }
    return [...byLinkId.values()];
  }, [data?.records]);

  const visible = pending.slice(0, MAX_ROWS);

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between px-6 pt-5 pb-1">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-foreground">Knowledge Base</h2>
          {pending.length > 0 && <Badge variant="default">{pending.length}</Badge>}
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/knowledge-base">View All</Link>
        </Button>
      </div>
      <p className="px-6 pb-4 text-sm text-muted-foreground">
        These articles need your attention.
      </p>

      <div className="border-t border-border">
        {isLoading ? (
          <div className="px-6 py-4 space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-md" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-40" />
                  <Skeleton className="h-3 w-28" />
                </div>
                <Skeleton className="h-3 w-20" />
              </div>
            ))}
          </div>
        ) : error ? (
          <p className="px-6 py-8 text-sm text-destructive text-center">
            Failed to load the knowledge base.
          </p>
        ) : visible.length === 0 ? (
          <div className="px-6 py-10 flex flex-col items-center gap-2 text-center">
            <BookOpen className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Nothing waiting on you. Anything Ellie proposes will show up here for review.
            </p>
          </div>
        ) : (
          <>
            {visible.map((p) => (
              <PendingRow key={p.item.id} pending={p} />
            ))}
            {pending.length > visible.length && (
              <Link
                to="/knowledge-base"
                className="block px-6 py-3 text-sm text-primary hover:underline border-t border-border"
              >
                {pending.length - visible.length} more waiting for review
              </Link>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
